import React from 'react';
import { Link } from 'react-router-dom';

const EULA = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <Link to="/" className="text-zinc-500 hover:text-zinc-900 text-sm uppercase tracking-wider mb-8 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-light text-zinc-900 mb-4">
            End-User License Agreement
          </h1>
          <p className="text-zinc-500 text-sm">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-zinc max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              This End-User License Agreement ("Agreement") is a legal agreement between you ("User" or "you") and Arbcor Systems ("Company," "we," "us," or "our") governing your use of the Arbcor platform, software, and related services (collectively, the "Service").
            </p>
            <p className="text-zinc-600 leading-relaxed">
              By accessing or using the Service, you agree to be bound by this Agreement. If you do not agree to these terms, do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">2. License Grant</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              Subject to your compliance with this Agreement, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes.
            </p>
            <p className="text-zinc-600 leading-relaxed">
              This license does not include the right to:
            </p>
            <ul className="list-disc list-inside text-zinc-600 mt-2 space-y-1">
              <li>Modify, copy, or create derivative works of the Service</li>
              <li>Reverse engineer, disassemble, or decompile the Service</li>
              <li>Rent, lease, lend, sell, or sublicense the Service</li>
              <li>Use the Service for any unlawful purpose</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">3. User Accounts</h2>
            <p className="text-zinc-600 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">4. Third-Party Integrations</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              The Service may integrate with third-party services, including but not limited to Intuit QuickBooks. Your use of such third-party services is subject to their respective terms of service and privacy policies.
            </p>
            <p className="text-zinc-600 leading-relaxed">
              We are not responsible for the availability, accuracy, or content of third-party services, and we do not endorse or assume any responsibility for any third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">5. Data and Privacy</h2>
            <p className="text-zinc-600 leading-relaxed">
              Your use of the Service is also governed by our <Link to="/privacy" className="text-zinc-900 underline hover:text-zinc-600">Privacy Policy</Link>, which describes how we collect, use, and protect your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">6. Intellectual Property</h2>
            <p className="text-zinc-600 leading-relaxed">
              The Service and all content, features, and functionality are owned by Arbcor Systems and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-zinc-600 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-zinc-600 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ARBCOR SYSTEMS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">9. Termination</h2>
            <p className="text-zinc-600 leading-relaxed">
              We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason, including if you breach this Agreement. Upon termination, your right to use the Service will cease immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">10. Changes to This Agreement</h2>
            <p className="text-zinc-600 leading-relaxed">
              We reserve the right to modify this Agreement at any time. We will notify you of any changes by posting the new Agreement on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">11. Contact Us</h2>
            <p className="text-zinc-600 leading-relaxed">
              If you have any questions about this Agreement, please contact us at:
            </p>
            <p className="text-zinc-600 mt-2">
              <a href="mailto:contact@arbcor.com" className="text-zinc-900 underline hover:text-zinc-600">contact@arbcor.com</a>
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-zinc-200 text-zinc-400 text-xs tracking-wider text-center">
          &copy; {new Date().getFullYear()} Arbcor Systems
        </div>
      </div>
    </div>
  );
};

export default EULA;
